import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Database } from '@cafe-pos/supabase';
import { parseNotifyEmails } from '@cafe-pos/shared';
import { supabase } from '../supabase.js';
import { buildBookingWidgetUrl, buildBookingWidgetEmbed } from '../bookingWidget.js';
import { Check, Pencil, Trash2 } from 'lucide-react';
import { Callout, PageHeader } from '../ui/index.js';

type RoomRow = Database['public']['Tables']['rooms']['Row'];

interface RoomForm {
  name: string;
  capacity: number;
  price_amd: number;
  active: boolean;
  sort_order: number;
  photos: string[];
}

const BLANK: RoomForm = {
  name: '',
  capacity: 2,
  price_amd: 0,
  active: true,
  sort_order: 0,
  photos: [],
};

const DEFAULT_WIDGET_ACCENT = '#0e7490';
const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

function normalizeAccent(value: string | null | undefined): string {
  const raw = (value ?? '').trim();
  const withHash = raw.startsWith('#') ? raw : `#${raw}`;
  return HEX_RE.test(withHash) ? withHash.toLowerCase() : DEFAULT_WIDGET_ACCENT;
}

function feedUrl(roomId: string, token: string): string {
  const base = (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '';
  return `${base}/functions/v1/ical-export?room=${roomId}&token=${token}`;
}

export function RoomsManager({ clientId }: { clientId: string | null }) {
  const { t } = useTranslation();
  const [rooms, setRooms] = useState<RoomRow[]>([]);
  const [icalToken, setIcalToken] = useState<string | null>(null);
  const [widgetToken, setWidgetToken] = useState<string | null>(null);
  const [hdmDepartmentId, setHdmDepartmentId] = useState(1);
  const [hdmDeptDraft, setHdmDeptDraft] = useState('1');
  const [hdmSaving, setHdmSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<'create' | string | null>(null);
  const [form, setForm] = useState<RoomForm>(BLANK);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [rotateConfirm, setRotateConfirm] = useState(false);
  const [widgetCopied, setWidgetCopied] = useState<'link' | 'embed' | null>(null);
  const [widgetTitle, setWidgetTitle] = useState('');
  const [widgetTitleDraft, setWidgetTitleDraft] = useState('');
  const [widgetAccent, setWidgetAccent] = useState(DEFAULT_WIDGET_ACCENT);
  const [widgetAccentDraft, setWidgetAccentDraft] = useState(DEFAULT_WIDGET_ACCENT);
  const [widgetTitleSaving, setWidgetTitleSaving] = useState(false);
  const [notifyEmails, setNotifyEmails] = useState<string[]>([]);
  const [notifyEmailsDraft, setNotifyEmailsDraft] = useState('');
  const [notifySaving, setNotifySaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  async function load() {
    if (!supabase || !clientId) return;
    setLoading(true);
    setError(null);
    const [roomsRes, settingsRes] = await Promise.all([
      supabase
        .from('rooms')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true }),
      supabase.from('booking_settings').select('ical_token, widget_token, widget_title, widget_accent, notify_emails, hdm_department_id').eq('client_id', clientId).maybeSingle(),
    ]);
    if (roomsRes.error) setError(roomsRes.error.message);
    else setRooms((roomsRes.data ?? []) as RoomRow[]);

    if (settingsRes.data) {
      setIcalToken(settingsRes.data.ical_token);
      setWidgetToken(settingsRes.data.widget_token);
      setWidgetTitle(settingsRes.data.widget_title ?? '');
      setWidgetTitleDraft(settingsRes.data.widget_title ?? '');
      const emails = parseNotifyEmails(settingsRes.data.notify_emails);
      setNotifyEmails(emails);
      setNotifyEmailsDraft(emails.join(', '));
      const accent = normalizeAccent(settingsRes.data.widget_accent);
      setWidgetAccent(accent);
      setWidgetAccentDraft(accent);
      const dept = settingsRes.data.hdm_department_id ?? 1;
      setHdmDepartmentId(dept);
      setHdmDeptDraft(String(dept));
    } else {
      // First visit: provision the secret feed token for this client.
      const { data: created, error: err } = await supabase
        .from('booking_settings')
        .insert({ client_id: clientId })
        .select('ical_token, widget_token, widget_title, widget_accent, notify_emails, hdm_department_id')
        .single();
      if (!err && created) {
        setIcalToken(created.ical_token);
        setWidgetToken(created.widget_token);
        setWidgetTitle(created.widget_title ?? '');
        setWidgetTitleDraft(created.widget_title ?? '');
        const emails = parseNotifyEmails(created.notify_emails);
        setNotifyEmails(emails);
        setNotifyEmailsDraft(emails.join(', '));
        const accent = normalizeAccent(created.widget_accent);
        setWidgetAccent(accent);
        setWidgetAccentDraft(accent);
        const dept = created.hdm_department_id ?? 1;
        setHdmDepartmentId(dept);
        setHdmDeptDraft(String(dept));
      }
    }
    setLoading(false);
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  function openCreate() {
    setForm({ ...BLANK, sort_order: rooms.length });
    setModal('create');
    setError(null);
  }

  function openEdit(row: RoomRow) {
    setForm({
      name: row.name,
      capacity: row.capacity,
      price_amd: Number(row.price_amd) || 0,
      active: row.active,
      sort_order: row.sort_order,
      photos: Array.isArray(row.photos) ? row.photos : [],
    });
    setModal(row.id);
    setError(null);
  }

  function closeModal() {
    setModal(null);
    setError(null);
  }

  async function handleSave() {
    if (!supabase || !clientId) return;
    if (!form.name.trim()) {
      setError(t('rooms.nameRequired'));
      return;
    }
    if (!(form.price_amd > 0)) {
      setError(t('rooms.priceRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    const payload = {
      name: form.name.trim(),
      capacity: Math.max(1, form.capacity),
      price_amd: Math.max(0, form.price_amd),
      active: form.active,
      sort_order: form.sort_order,
      photos: form.photos,
      updated_at: new Date().toISOString(),
    };
    const { error: err } =
      modal === 'create'
        ? await supabase.from('rooms').insert({ client_id: clientId, ...payload })
        : await supabase.from('rooms').update(payload).eq('id', modal!);
    if (err) {
      setError(err.message);
      setSaving(false);
      return;
    }
    setSaving(false);
    closeModal();
    void load();
  }

  async function handleDelete(id: string) {
    if (!supabase) return;
    const { error: err } = await supabase.from('rooms').delete().eq('id', id);
    if (err) setError(err.message);
    else {
      setDeleteConfirmId(null);
      void load();
    }
  }

  async function copyFeed(roomId: string) {
    if (!icalToken) return;
    try {
      await navigator.clipboard.writeText(feedUrl(roomId, icalToken));
      setCopiedId(roomId);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      /* clipboard unavailable — URL is visible in the title anyway */
    }
  }

  async function rotateToken() {
    if (!supabase || !clientId) return;
    const next = crypto.randomUUID();
    const { error: err } = await supabase
      .from('booking_settings')
      .update({ ical_token: next, updated_at: new Date().toISOString() })
      .eq('client_id', clientId);
    if (err) setError(err.message);
    else setIcalToken(next);
    setRotateConfirm(false);
  }

  async function handlePhotoUpload(files: FileList | null) {
    if (!supabase || !clientId || !files || files.length === 0) return;
    setUploading(true);
    setError(null);
    for (const file of Array.from(files)) {
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
      const path = `${clientId}/${crypto.randomUUID()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from('room-photos')
        .upload(path, file, { contentType: file.type || 'image/jpeg' });
      if (upErr) {
        setError(upErr.message);
        continue;
      }
      const { data } = supabase.storage.from('room-photos').getPublicUrl(path);
      if (data.publicUrl) {
        setForm((f) => ({ ...f, photos: [...f.photos, data.publicUrl] }));
      }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function removePhoto(url: string) {
    setForm((f) => ({ ...f, photos: f.photos.filter((p) => p !== url) }));
  }

  async function copyWidget(kind: 'link' | 'embed') {
    if (!widgetToken) return;
    const text =
      kind === 'link' ? buildBookingWidgetUrl(widgetToken) : buildBookingWidgetEmbed(widgetToken);
    try {
      await navigator.clipboard.writeText(text);
      setWidgetCopied(kind);
      setTimeout(() => setWidgetCopied(null), 1500);
    } catch {
      /* clipboard unavailable */
    }
  }

  async function saveWidgetTitle() {
    if (!supabase || !clientId) return;
    const title = widgetTitleDraft.trim();
    const accent = normalizeAccent(widgetAccentDraft);
    setWidgetTitleSaving(true);
    setError(null);
    const { error: err } = await supabase
      .from('booking_settings')
      .update({
        widget_title: title || null,
        widget_accent: accent,
        updated_at: new Date().toISOString(),
      })
      .eq('client_id', clientId);
    setWidgetTitleSaving(false);
    if (err) setError(err.message);
    else {
      setWidgetTitle(title);
      setWidgetTitleDraft(title);
      setWidgetAccent(accent);
      setWidgetAccentDraft(accent);
    }
  }

  async function saveNotifyEmails() {
    if (!supabase || !clientId) return;
    const emails = parseNotifyEmails(notifyEmailsDraft);
    setNotifySaving(true);
    setError(null);
    const { error: err } = await supabase
      .from('booking_settings')
      .update({
        notify_emails: emails,
        updated_at: new Date().toISOString(),
      })
      .eq('client_id', clientId);
    setNotifySaving(false);
    if (err) setError(err.message);
    else {
      setNotifyEmails(emails);
      setNotifyEmailsDraft(emails.join(', '));
    }
  }

  async function saveHdmDepartment() {
    if (!supabase || !clientId) return;
    const dept = Math.max(1, Number(hdmDeptDraft) || 1);
    setHdmSaving(true);
    setError(null);
    const { error: err } = await supabase
      .from('booking_settings')
      .update({ hdm_department_id: dept, updated_at: new Date().toISOString() })
      .eq('client_id', clientId);
    setHdmSaving(false);
    if (err) setError(err.message);
    else {
      setHdmDepartmentId(dept);
      setHdmDeptDraft(String(dept));
    }
  }

  return (
    <section className="ui-stack">
      <PageHeader
        title={t('rooms.title')}
        subtitle={t('rooms.subtitle')}
        actions={
          <button className="btn" onClick={openCreate}>
            + {t('rooms.add')}
          </button>
        }
      />

      {error && !modal && <Callout tone="danger">{error}</Callout>}

      <div className="panel rooms-table">
        {loading ? (
          <div className="empty-state">{t('common.loading')}</div>
        ) : rooms.length === 0 ? (
          <div className="empty-state">{t('rooms.empty')}</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t('rooms.colName')}</th>
                <th>{t('rooms.colCapacity')}</th>
                <th>{t('rooms.colPrice')}</th>
                <th>{t('rooms.colActive')}</th>
                <th>{t('rooms.colFeed')}</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {rooms.map((r) => (
                <tr key={r.id} style={!r.active ? { opacity: 0.55 } : undefined}>
                  <td>{r.name}</td>
                  <td>{r.capacity}</td>
                  <td>{Number(r.price_amd || 0).toLocaleString()} ֏</td>
                  <td>{r.active ? t('rooms.activeYes') : t('rooms.activeNo')}</td>
                  <td>
                    {icalToken ? (
                      <button
                        className="btn-ghost"
                        onClick={() => void copyFeed(r.id)}
                        title={feedUrl(r.id, icalToken)}
                      >
                        {copiedId === r.id ? t('rooms.copied') : t('rooms.copyFeed')}
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    <div className="row-actions">
                      <button className="btn-icon" onClick={() => openEdit(r)} title={t('common.save')}>
                        <Pencil className="ui-i" />
                      </button>
                      {deleteConfirmId === r.id ? (
                        <>
                          <button
                            className="btn-icon danger"
                            onClick={() => void handleDelete(r.id)}
                            title={t('common.confirmDelete')}
                          >
                            <Check className="ui-i" />
                          </button>
                          <button
                            className="btn-icon"
                            onClick={() => setDeleteConfirmId(null)}
                            title={t('common.cancel')}
                          >
                            ×
                          </button>
                        </>
                      ) : (
                        <button
                          className="btn-icon danger"
                          onClick={() => setDeleteConfirmId(r.id)}
                          title={t('common.delete')}
                        >
                          <Trash2 className="ui-i" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {widgetToken && (
        <div className="panel rooms-feed-panel">
          <h3>{t('rooms.widgetTitle')}</h3>
          <p className="rooms-feed-hint">{t('rooms.widgetHint')}</p>
          <div className="rooms-feed-actions" style={{ marginBottom: 16 }}>
            <label className="form-field" style={{ maxWidth: 320, margin: 0 }}>
              <span className="form-label">{t('rooms.widgetTitleLabel')}</span>
              <input
                value={widgetTitleDraft}
                maxLength={120}
                placeholder={t('rooms.widgetTitlePlaceholder')}
                onChange={(e) => setWidgetTitleDraft(e.target.value)}
              />
            </label>
            <label className="form-field" style={{ margin: 0 }}>
              <span className="form-label">{t('rooms.widgetColorLabel')}</span>
              <span className="rooms-widget-color">
                <input
                  type="color"
                  value={HEX_RE.test(widgetAccentDraft) ? widgetAccentDraft : DEFAULT_WIDGET_ACCENT}
                  onChange={(e) => setWidgetAccentDraft(e.target.value.toLowerCase())}
                  title={t('rooms.widgetColorHint')}
                />
                <input
                  value={widgetAccentDraft}
                  maxLength={7}
                  spellCheck={false}
                  onChange={(e) => setWidgetAccentDraft(e.target.value)}
                />
              </span>
            </label>
          </div>
          <div className="rooms-feed-actions" style={{ marginBottom: 16 }}>
            <button
              className="btn"
              disabled={
                widgetTitleSaving ||
                (widgetTitleDraft.trim() === widgetTitle &&
                  normalizeAccent(widgetAccentDraft) === widgetAccent)
              }
              onClick={() => void saveWidgetTitle()}
            >
              {widgetTitleSaving ? '…' : t('common.save')}
            </button>
          </div>
          <div className="rooms-widget-link">
            <code>{buildBookingWidgetUrl(widgetToken)}</code>
          </div>
          <div className="rooms-feed-actions">
            <a
              className="btn-ghost"
              href={buildBookingWidgetUrl(widgetToken)}
              target="_blank"
              rel="noreferrer"
            >
              {t('rooms.widgetOpen')}
            </a>
            <button className="btn-ghost" onClick={() => void copyWidget('link')}>
              {widgetCopied === 'link' ? t('rooms.copied') : t('rooms.widgetCopyLink')}
            </button>
            <button className="btn-ghost" onClick={() => void copyWidget('embed')}>
              {widgetCopied === 'embed' ? t('rooms.copied') : t('rooms.widgetCopyEmbed')}
            </button>
          </div>
        </div>
      )}

      {widgetToken && (
        <div className="panel rooms-feed-panel">
          <h3>{t('rooms.notifyEmailsTitle')}</h3>
          <p className="rooms-feed-hint">{t('rooms.notifyEmailsHint')}</p>
          <label className="form-field rooms-notify-emails">
            <span className="form-label">{t('rooms.notifyEmails')}</span>
            <textarea
              rows={3}
              value={notifyEmailsDraft}
              placeholder={t('rooms.notifyEmailsPlaceholder')}
              onChange={(e) => setNotifyEmailsDraft(e.target.value)}
            />
          </label>
          <div className="rooms-feed-actions">
            <button
              className="btn"
              disabled={
                notifySaving ||
                parseNotifyEmails(notifyEmailsDraft).join(',') === notifyEmails.join(',')
              }
              onClick={() => void saveNotifyEmails()}
            >
              {notifySaving ? '…' : t('common.save')}
            </button>
          </div>
        </div>
      )}

      {icalToken && (
        <div className="panel rooms-feed-panel">
          <h3>{t('rooms.hdmTitle')}</h3>
          <p className="rooms-feed-hint">{t('rooms.hdmHint')}</p>
          <div className="rooms-feed-actions" style={{ marginBottom: 20 }}>
            <label className="form-field" style={{ maxWidth: 160, margin: 0 }}>
              <span className="form-label">{t('rooms.hdmDept')}</span>
              <input
                type="number"
                min={1}
                value={hdmDeptDraft}
                onChange={(e) => setHdmDeptDraft(e.target.value)}
                title={t('rooms.hdmDeptTitle')}
              />
            </label>
            <button
              className="btn"
              disabled={hdmSaving || Number(hdmDeptDraft) === hdmDepartmentId}
              onClick={() => void saveHdmDepartment()}
              style={{ alignSelf: 'flex-end' }}
            >
              {hdmSaving ? '…' : t('common.save')}
            </button>
          </div>

          <h3>{t('rooms.feedTitle')}</h3>
          <p className="rooms-feed-hint">{t('rooms.feedHint')}</p>
          {rotateConfirm ? (
            <div className="rooms-feed-actions">
              <span style={{ fontSize: 13 }}>{t('rooms.rotateConfirm')}</span>
              <button className="btn danger" onClick={() => void rotateToken()}>
                {t('rooms.rotateYes')}
              </button>
              <button className="btn-ghost" onClick={() => setRotateConfirm(false)}>
                {t('common.cancel')}
              </button>
            </div>
          ) : (
            <button className="btn-ghost" onClick={() => setRotateConfirm(true)}>
              {t('rooms.rotate')}
            </button>
          )}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modal === 'create' ? t('rooms.add') : t('rooms.edit')}</h2>
              <button className="btn-icon" onClick={closeModal}>
                ×
              </button>
            </div>

            {error && <div className="error-banner" style={{ margin: '0 0 12px' }}>{error}</div>}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <label className="form-field">
                <span className="form-label">
                  {t('rooms.colName')}
                  <span className="form-required">*</span>
                </span>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  autoFocus
                  placeholder={t('rooms.namePlaceholder')}
                  onKeyDown={(e) => e.key === 'Enter' && void handleSave()}
                />
              </label>

              <label className="form-field">
                <span className="form-label">{t('rooms.colCapacity')}</span>
                <input
                  type="number"
                  min={1}
                  value={form.capacity}
                  onChange={(e) => setForm({ ...form, capacity: Number(e.target.value) || 1 })}
                />
              </label>

              <label className="form-field">
                <span className="form-label">
                  {t('rooms.colPrice')}
                  <span className="form-required">*</span>
                </span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={form.price_amd || ''}
                  onChange={(e) => setForm({ ...form, price_amd: Number(e.target.value) || 0 })}
                  placeholder={t('rooms.pricePlaceholder')}
                />
              </label>

              <label className="form-field">
                <span className="form-label">{t('rooms.colSort')}</span>
                <input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) || 0 })}
                />
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                />
                {t('rooms.colActive')}
              </label>

              <div className="form-field">
                <span className="form-label">{t('rooms.photos')}</span>
                {form.photos.length > 0 && (
                  <div className="rooms-photo-grid">
                    {form.photos.map((url) => (
                      <div key={url} className="rooms-photo-thumb">
                        <img src={url} alt="" loading="lazy" />
                        <button
                          type="button"
                          className="rooms-photo-remove"
                          onClick={() => removePhoto(url)}
                          title={t('common.delete')}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => void handlePhotoUpload(e.target.files)}
                />
                <button
                  type="button"
                  className="btn-ghost"
                  style={{ alignSelf: 'flex-start' }}
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploading ? '…' : `+ ${t('rooms.addPhoto')}`}
                </button>
                <span className="text-muted" style={{ fontSize: 12 }}>
                  {t('rooms.photosHint')}
                </span>
              </div>
            </div>

            <div className="modal-actions" style={{ marginTop: 20 }}>
              <button className="btn-ghost" onClick={closeModal} disabled={saving}>
                {t('common.cancel')}
              </button>
              <button className="btn btn-primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? '…' : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
